import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import {
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import {
  useDeleteMessageMutation,
  useGetMessagesQuery,
} from "../../services/message.query";
import DeleteConfirmationDialog from "../../layouts/delete-confirm";
import { DefaultPage } from "../../layouts/default-page";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { RowActions } from "../common/RowActions";
import { ExportLoadingBackdrop } from "../common/ExportLoadingBackdrop";
import { useTableState } from "../../hooks/useTableState";
import { Notify } from "../../layouts/notify";

export default function MessageHistory() {
  const defaultFilters = useMemo(
    () => ({
      search: "",
    }),
    []
  );

  const {
    page,
    pageSize,
    filters,
    hasActiveFilters,
    setPage,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
  });

  const [draftSearch, setDraftSearch] = useState(filters.search);

  useEffect(() => {
    setDraftSearch(filters.search);
  }, [filters]);

  const [open, setOpen] = useState<boolean>(false);
  const [itemTobeDeleted, setItemTobeDelete] = useState<any>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const [deleteMessageItem, { isLoading: deleteLoading }] =
    useDeleteMessageMutation();

  const handleClose = () => {
    setOpen(false);
  };

  const openDeleteModal = (row: any) => {
    setOpen(true);
    setItemTobeDelete(row);
  };

  const deleteItem = async () => {
    try {
      await deleteMessageItem(itemTobeDeleted?.id).unwrap();
      Notify("success", "Message deleted successfully.");
      setOpen(false);
    } catch (err: any) {
      Notify("error", err?.message || "Failed to delete message.");
      setOpen(false);
    }
  };

  const {
    data: messages = [],
    isLoading,
    isError,
    isFetching,
  } = useGetMessagesQuery("");

  const handleSearch = () => {
    setFilters({
      search: draftSearch.trim(),
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const filteredData = useMemo(() => {
    if (!filters.search) return messages;
    const query = filters.search.toLowerCase();
    return messages?.filter((pr: any) => {
      const searchContent = `${pr?.message || ""} ${pr?.user_type || ""}`.toLowerCase();
      return searchContent.includes(query);
    });
  }, [messages, filters.search]);

  const totalCount = filteredData.length;
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const handleExportPDF = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Preparing PDF for all ${filteredData.length.toLocaleString()} messages...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const doc = new jsPDF("landscape", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Sent Notification Broadcast History", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Total Messages: ${filteredData.length}`,
        14,
        22
      );

      const tableColumn = ["#", "Date Sent", "Target Audience", "Message Content"];
      const tableRows = filteredData.map((item: any, idx: number) => [
        idx + 1,
        item.created_at ? format(new Date(item.created_at), "MM/dd/yyyy hh:mm a") : "—",
        item.user_type || "ALL",
        String(item.message || "—"),
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Message_History_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${filteredData.length} messages to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Generating Excel spreadsheet for ${filteredData.length.toLocaleString()} messages...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const rows = filteredData.map((item: any, idx: number) => ({
        "No.": idx + 1,
        "Date Sent": item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd hh:mm a") : "—",
        "Target Audience": item.user_type || "ALL",
        "Message Content": item.message || "—",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Message History");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 22 },
        { wch: 18 },
        { wch: 60 },
      ];

      XLSX.writeFile(wb, `MTRCB_Message_History_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${filteredData.length} messages to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "number",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Typography>{(page - 1) * pageSize + row.index + 1}</Typography>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Date Sent",
        size: 180,
        Cell: ({ cell }: any) =>
          cell.getValue()
            ? format(new Date(cell.getValue()), "MMM dd, yyyy · hh:mm a")
            : "—",
      },
      {
        accessorKey: "user_type",
        header: "Audience",
        size: 150,
        Cell: ({ cell }: any) => cell.getValue() || "ALL",
      },
      {
        accessorKey: "message",
        header: "Message Content",
        size: 400,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13.5, color: "#1E293B" }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <RowActions
            actions={[
              {
                label: "Delete",
                tone: "error",
                onClick: () => openDeleteModal(row.original),
              },
            ]}
          />
        ),
      },
    ],
    [page, pageSize]
  );

  return (
    <DefaultPage
      title="Message History"
      subTitle="Review and manage sent broadcast announcements and notifications."
      loading={isLoading}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays historical broadcast messages sent to mobile users and deputies.
        </Alert>
      </Box>

      {isError && (
        <Box mb={2.5}>
          <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Failed to load message history.
          </Alert>
        </Box>
      )}

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              size="small"
              label="Search Message / Audience"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(totalCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={paginatedData}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: any) => row.id}
        isLoading={isLoading}
        isFetching={isFetching}
      />

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />

      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={deleteItem}
        loading={deleteLoading}
        item={itemTobeDeleted?.message}
        itemName={itemTobeDeleted?.message}
      />
    </DefaultPage>
  );
}
